import { Box, Skeleton } from "@mui/material";
import React from "react";
import type { WorkspaceUser } from "../../../types/workspaceUser";
import { VirtualTourCommentSignedUrlResponseData } from "api/virtual-tour/queries/useGetVirtualTourCommentAttachmentsQuery";

interface V1Mention {
  id: string;
  name: string;
  email: string;
  picture: string;
  value: string;
  prefix: string;
}

export function parseComment(
  text: string, 
  users: WorkspaceUser[],
  annotation?: VirtualTourAnnotation | VirtualTourAnnotationComment,
  signedUrls?: VirtualTourCommentSignedUrlResponseData,
  onImageClick?: (url: string) => void
): React.ReactNode[] {
  // if the annotation is a measurement, return an empty array
  if (annotation?.type !== 'TEXT') {
    return [];
  }
  // get the paragraphs
  const paragraphs = text.split(/\n\n+/);
  // get the version and attachments
  const version = annotation.version;
  const isV1 = version === 1 || version === undefined;

  return paragraphs.map((paragraph, pIndex) => {
    const parts: React.ReactNode[] = [];
    let currentIndex = 0;

    if (isV1) {
      // parse v1 mentions
      const regex = /\[\[(.*?)\]\]/g;
      let match: RegExpExecArray | null;

      while ((match = regex.exec(paragraph)) !== null) {
        if (match.index > currentIndex) {
          parts.push(paragraph.slice(currentIndex, match.index));
        }

        try {
          const mentionData: V1Mention = JSON.parse(match[1]);
          const user = users.find(u => u.user.id === mentionData.id);
          parts.push(
            <Box
              key={`mention-${pIndex}-${match.index}`}
              component="span"
              sx={{
                color: '#58a6ff',
                backgroundColor: 'rgba(88, 166, 255, 0.1)',
                padding: '0.1rem 0.3rem',
                borderRadius: '3px',
              }}
            >
              @{user?.user.name || mentionData.name}
            </Box>
          );
        } catch (e) {
          parts.push(match[0]);
        }

        currentIndex = match.index + match[0].length;
      }
    } else {
      // parse v2 mentions
      const regex = /\[\[(\(@\)\(([^)]+)\)([^\]]+)|\*\*([^*]+)\*\*|\*([^*]+)\*|\(img\)([^\]]+))\]\]/g;
      let match: RegExpExecArray | null;

      // parse all the matches
      while ((match = regex.exec(paragraph)) !== null) {
        // if match is found later in the paragraph, push the text before it
        if (match.index > currentIndex) {
          parts.push(paragraph.slice(currentIndex, match.index));
        }

        // if match is a mention (has @)
        if (match[2] && match[3]) {
          const userId = match[2];
          const displayName = match[3];
          const user = users.find(u => u.user.id === userId);

          parts.push(
            <Box
              key={`mention-${pIndex}-${match.index}`}
              component="span"
              sx={{
                color: '#58a6ff',
                backgroundColor: 'rgba(88, 166, 255, 0.1)',
                padding: '0.1rem 0.3rem',
                borderRadius: '3px',
              }}
            >
              @{user?.user.name || displayName}
            </Box>
          );
        } else if (match[4]) {
          // if match is bold
          parts.push(
            <span
              key={`bold-${pIndex}-${match.index}`}
              style={{ fontWeight: 'bold' }}
            >
              {match[4]}
            </span>
          );
        } else if (match[5]) {
          // if match is italic
          parts.push(
            <span
              key={`italic-${pIndex}-${match.index}`}
              style={{ fontStyle: 'italic' }}
            >
              {match[5]}
            </span>
          );
        } else if (match[6]) {
          // if match is an image
          if (!signedUrls) {
            parts.push(
              <Skeleton
                key={`image-${pIndex}-${match.index}`}
                variant="rectangular"
                animation="wave"
                width="6rem"
                height="6rem"
              />
            );
          } else {
            const filename = match[6];
            const signedUrl = signedUrls.viewUrls.find(url => url.filename === filename)?.signedUrl;
            
            if (signedUrl) {
              parts.push(
                <Box
                  key={`image-${pIndex}-${match.index}`}
                  component="img"
                  src={signedUrl}
                  alt={filename}
                  onClick={() => onImageClick?.(signedUrl)}
                  sx={{
                    maxWidth: '100%',
                    maxHeight: '300px',
                    borderRadius: '4px',
                    mt: 1,
                    mb: 1,
                    cursor: 'pointer',
                    transition: 'transform 0.2s',
                    '&:hover': {
                      transform: 'scale(1.02)',
                    }
                  }}
                />
              );
            }
          }
        }

        currentIndex = match.index + match[0].length;
      }
    }

    // push the remaining text
    if (currentIndex < paragraph.length) {
      parts.push(paragraph.slice(currentIndex));
    }

    return (
      <React.Fragment key={`p-${pIndex}`}>
        {parts}
        {pIndex < paragraphs.length - 1 && <br />}
        {pIndex < paragraphs.length - 1 && <br />}
      </React.Fragment>
    );
  });
} 